import * as pdfjsLib from 'pdfjs-dist';

// Set the worker source for PDF.js - use local worker file for better reliability
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

/**
 * Validate and sanitize path to prevent path traversal attacks
 * @param path - The path to validate
 * @returns Sanitized path or null if invalid
 */
const sanitizePath = (path: string): string | null => {
  // Check for path traversal attempts
  if (path.includes('..') || path.includes('//') || path.startsWith('/')) {
    return null;
  }
  
  // Validate path format
  if (!/^[a-zA-Z0-9_-]+$/.test(path)) {
    return null;
  }
  
  return path;
};

/**
 * Generate the URL for a PDF based on the record ID and optional path
 * @param recordId - The Airtable record ID (must start with 'rec')
 * @param path - Optional path prefix (e.g., 'offers', 'contracts')
 * @returns The URL to access the PDF
 * @throws {Error} If path contains invalid characters or traversal attempts
 * @example
 * generatePDFUrl('rec123', 'offers') // 'https://storage.googleapis.com/xray-pdfs/offers/rec123.pdf'
 * generatePDFUrl('rec123') // 'https://storage.googleapis.com/xray-pdfs/rec123.pdf'
 */
export const generatePDFUrl = (recordId: string, path?: string): string => {
  // Validate and sanitize path if provided
  if (path) {
    const sanitizedPath = sanitizePath(path);
    if (!sanitizedPath) {
      throw new Error('Invalid path format');
    }
    return `https://storage.googleapis.com/xray-pdfs/${sanitizedPath}/${recordId}.pdf`;
  }
  return `https://storage.googleapis.com/xray-pdfs/${recordId}.pdf`;
};

/**
 * Parse URL path to extract path and record ID
 * @param urlPath - The URL path (e.g., '/offers/recXXXX' or '/recXXXX')
 * @returns Object with path and recordId
 * @throws {Error} If URL format is invalid
 * @example
 * parsePDFUrl('/offers/rec123') // { path: 'offers', recordId: 'rec123' }
 * parsePDFUrl('/rec123') // { recordId: 'rec123' }
 */
export const parsePDFUrl = (urlPath: string): { path?: string; recordId: string } => {
  const segments = urlPath.split('/').filter(Boolean);
  
  if (segments.length === 1) {
    // Direct record ID: /recXXXX
    return { recordId: segments[0] };
  } else if (segments.length === 2) {
    // Path + record ID: /offers/recXXXX
    return { path: segments[0], recordId: segments[1] };
  }
  
  throw new Error('Invalid PDF URL format');
};

/**
 * Load a PDF document using PDF.js with enhanced error handling
 * @param url - The URL of the PDF to load
 * @returns Promise that resolves to the PDF document
 * @throws {Error} If PDF fails to load with specific error messages
 * @example
 * const doc = await loadPDFDocument('https://example.com/document.pdf');
 */
export const loadPDFDocument = async (url: string): Promise<any> => {
  try {
    // Create loading task with additional options for better compatibility
    const loadingTask = pdfjsLib.getDocument({
      url: url,
      httpHeaders: {
        'Accept': 'application/pdf',
      },
      withCredentials: false, // Don't send cookies for cross-origin requests
    });
    
    const doc = await loadingTask.promise;
    return doc;
  } catch (error) {
    // Provide more specific error messages
    if (error instanceof Error) {
      if (error.message.includes('CORS')) {
        throw new Error('PDF access blocked by CORS policy. Please check server configuration.');
      } else if (error.message.includes('404')) {
        throw new Error('PDF not found. Please verify the record ID and path.');
      } else if (error.message.includes('403')) {
        throw new Error('Access denied. Please check file permissions.');
      } else if (error.message.includes('NetworkError')) {
        throw new Error('Network error. Please check your internet connection.');
      }
    }
    
    throw new Error(`Failed to load PDF: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

/**
 * Render a PDF page to a canvas with enhanced error handling
 * @param page - The PDF page to render
 * @param canvas - The canvas element to render to
 * @param scale - The scale factor for rendering (default: 1.0)
 * @param rotation - The rotation angle in degrees (optional)
 * @returns Promise that resolves when rendering is complete
 * @throws {Error} If rendering fails
 * @example
 * await renderPDFPage(page, canvas, 1.5, 90);
 */
export const renderPDFPage = async (
  page: any,
  canvas: HTMLCanvasElement,
  scale: number = 1.0,
  rotation: number = 0
): Promise<void> => {
  try {
    const viewport = page.getViewport({ scale, rotation });
    const context = canvas.getContext('2d');

    if (!context) {
      throw new Error('Could not get canvas context');
    }

    // Set canvas dimensions
    canvas.height = viewport.height;
    canvas.width = viewport.width;

    // Clear the canvas
    context.clearRect(0, 0, canvas.width, canvas.height);

    const renderContext = {
      canvasContext: context,
      viewport: viewport,
    };

    await page.render(renderContext).promise;
  } catch (error) {
    throw new Error(`Failed to render PDF page: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

/**
 * Get information about a PDF page
 * @param page - The PDF page
 * @returns Object containing page information (width, height, rotation)
 * @example
 * const info = getPageInfo(page);
 * console.log(`Page size: ${info.width}x${info.height}`);
 */
export const getPageInfo = (page: any) => {
  const viewport = page.getViewport({ scale: 1.0 });
  return {
    width: viewport.width,
    height: viewport.height,
    rotation: viewport.rotation,
  };
};

/**
 * Validate a record ID format (Airtable record IDs start with 'rec')
 * @param recordId - The record ID to validate
 * @returns True if the record ID is valid
 * @example
 * validateRecordId('recibLkSVxBvnRfqW') // true
 * validateRecordId('123456') // false
 */
export const validateRecordId = (recordId: string): boolean => {
  // Airtable record IDs typically start with 'rec' and are alphanumeric
  const recordIdPattern = /^rec[A-Za-z0-9]+$/;
  return recordIdPattern.test(recordId);
};

/**
 * Validate a path format (alphanumeric with hyphens and underscores only)
 * @param path - The path to validate
 * @returns True if the path is valid
 * @example
 * validatePath('offers') // true
 * validatePath('path-with-hyphens') // true
 * validatePath('path<script>') // false
 */
export const validatePath = (path: string): boolean => {
  // Path should be alphanumeric with optional hyphens/underscores
  const pathPattern = /^[a-zA-Z0-9_-]+$/;
  return pathPattern.test(path);
};

/**
 * Format file size in human-readable format
 * @param bytes - File size in bytes
 * @returns Formatted file size string
 * @example
 * formatFileSize(1024) // '1 KB'
 * formatFileSize(1048576) // '1 MB'
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}; 